import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart,
  CheckSquare,
  Calendar,
  MessageSquare,
  BookOpen,
  Barcode,
  Timer,
  Baby,
  TrendingUp,
  Moon,
  AudioLines,
  ChevronRight,
} from 'lucide-react';
import { isNative } from '@/lib/capacitor';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { useToolMenuConfig } from '@/hooks/useToolMenuConfig';
import { useTranslation } from '@parentpath/i18n';

interface ToolItem {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  href: string;
  /** Only visible in baby mode */
  babyModeOnly?: boolean;
  /** Only visible in pregnancy mode */
  pregnancyModeOnly?: boolean;
}

// Feste Anzahl an Platzhalter-Kacheln waehrend des Loading-Gates - unabhaengig
// vom Modus, da genau der Modus zu diesem Zeitpunkt noch nicht feststeht.
const TOOL_SKELETON_COUNT = 6;

export function ToolsPage() {
  // Selektoren statt Vollabo: ToolsPage re-rendert damit nur noch, wenn sich
  // appMode, lastCompletedPregnancy oder appModeResolved tatsaechlich aendern -
  // nicht mehr bei jeder Aenderung irgendeines anderen Store-Felds
  // (currentPregnancy, currentSSW, isLoading, ...).
  const appMode = usePregnancyStore((state) => state.appMode);
  const lastCompletedPregnancy = usePregnancyStore((state) => state.lastCompletedPregnancy);
  const appModeResolved = usePregnancyStore((state) => state.appModeResolved);
  const isBabyCareEnabled = useFeatureEnabled('baby_care_enabled');
  const isBabyGrowthEnabled = useFeatureEnabled('baby_growth_enabled');
  const isSleepPlanEnabled = useFeatureEnabled('sleep_plan_enabled');
  // isLoading wurde bisher verworfen - dadurch rendete ToolsPage schon mit dem
  // leeren/noch nicht admin-konfigurierten Zustand, bevor die Konfiguration
  // geladen war, und sprang danach sichtbar auf die richtige Reihenfolge um.
  const { config: menuConfig, isLoading: isMenuConfigLoading } = useToolMenuConfig();
  const { t } = useTranslation('tools');

  // Feature-Flags bewusst NICHT im Gate: ihr Default ist true (siehe
  // useFeatureFlags.ts DEFAULT_FLAGS), Tools sind also im Normalfall schon vor
  // dem Laden korrekt sichtbar und wuerden hoechstens nachtraeglich versteckt
  // werden, wenn ein Flag tatsaechlich deaktiviert ist - kein Flacker-Risiko,
  // das ein Gate rechtfertigt.
  const showSkeleton = !appModeResolved || isMenuConfigLoading;

  // useMemo mit t als Dependency: TOOLS enthaelt uebersetzte Strings, muss also
  // bei Locale-Wechsel neu gebaut werden, aber nicht bei jedem Render.
  const TOOLS: ToolItem[] = useMemo(
    () => [
      {
        id: 'baby-care',
        name: t('items.babyCare.name'),
        description: t('items.babyCare.description'),
        icon: Baby,
        href: '/tools/baby-care',
        babyModeOnly: true,
      },
      {
        id: 'baby-growth',
        name: t('items.babyGrowth.name'),
        description: t('items.babyGrowth.description'),
        icon: TrendingUp,
        href: '/tools/growth',
        babyModeOnly: true,
      },
      {
        id: 'sleep-plan',
        name: t('items.sleepPlan.name'),
        description: t('items.sleepPlan.description'),
        icon: Moon,
        href: '/tools/sleep-plan',
        babyModeOnly: true,
      },
      {
        id: 'sleep-sounds',
        name: t('items.sleepSounds.name'),
        description: t('items.sleepSounds.description'),
        icon: AudioLines,
        href: '/tools/sleep-sounds',
        babyModeOnly: true,
      },
      {
        id: 'names',
        name: t('items.names.name'),
        description: t('items.names.description'),
        icon: Heart,
        href: '/tools/names',
        pregnancyModeOnly: true,
      },
      {
        id: 'checklists',
        name: t('items.checklists.name'),
        description: t('items.checklists.description'),
        icon: CheckSquare,
        href: '/tools/checklists',
      },
      {
        id: 'calendar',
        name: t('items.calendar.name'),
        description: t('items.calendar.description'),
        icon: Calendar,
        href: '/calendar',
      },
      {
        id: 'contractions',
        name: t('items.contractions.name'),
        description: t('items.contractions.description'),
        icon: Timer,
        href: '/tools/contractions',
        pregnancyModeOnly: true,
      },
      {
        id: 'chat',
        name: t('items.chat.name'),
        description: t('items.chat.description'),
        icon: MessageSquare,
        href: '/chat',
      },
      {
        id: 'guides',
        name: t('items.guides.name'),
        description: t('items.guides.description'),
        icon: BookOpen,
        href: '/tools/guides',
      },
      {
        id: 'produktcheck',
        name: t('items.produktcheck.name'),
        description: t('items.produktcheck.description'),
        icon: Barcode,
        href: '/tools/scanner',
      },
    ],
    [t]
  );

  // Determine mode-specific visibility
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';

  // Filter tools based on mode and feature flags
  const modeFilteredTools = useMemo(
    () =>
      TOOLS.filter((tool) => {
        // Hide pregnancy-only tools in baby mode
        if (tool.pregnancyModeOnly && isBabyMode) return false;
        // Hide baby-care when not in baby mode or feature is disabled
        if (tool.id === 'baby-care' && (!isBabyMode || !isBabyCareEnabled)) return false;
        // Hide baby-growth when not in baby mode or feature is disabled
        if (tool.id === 'baby-growth' && (!isBabyMode || isBabyGrowthEnabled === false)) return false;
        // Hide sleep-plan when not in baby mode or feature is disabled
        if (tool.id === 'sleep-plan' && (!isBabyMode || !isSleepPlanEnabled)) return false;
        // Hide other babyModeOnly tools when not in baby mode
        if (tool.babyModeOnly && !isBabyMode) return false;
        return true;
      }),
    [TOOLS, isBabyMode, isBabyCareEnabled, isBabyGrowthEnabled, isSleepPlanEnabled]
  );

  // Apply admin-configured ordering and visibility
  const modeConfig = isBabyMode ? menuConfig?.baby : menuConfig?.pregnancy;

  const visibleTools: ToolItem[] = useMemo(() => {
    if (!modeConfig || modeConfig.length === 0) {
      return modeFilteredTools;
    }

    // Build ordered list based on config
    const ordered: ToolItem[] = [];
    const toolMap = new Map(modeFilteredTools.map((tool) => [tool.id, tool]));

    for (const entry of modeConfig) {
      const tool = toolMap.get(entry.id);
      if (tool && entry.visible) {
        ordered.push(tool);
        toolMap.delete(entry.id);
      } else if (tool) {
        // Tool exists but is hidden via admin config
        toolMap.delete(entry.id);
      }
    }
    // Append any tools not in config (new tools added after config was saved)
    for (const tool of toolMap.values()) {
      ordered.push(tool);
    }
    return ordered;
  }, [modeFilteredTools, modeConfig]);

  return (
    <>
      {/* Logo - only for web (native has it in TopBar) */}
      {!isNative && (
        <div className="fixed top-4 left-4 z-50">
          <Link to="/">
            <img
              src="https://parentpath.de/pp_logo-piktogram-w100.svg"
              alt="ParentPath"
              className="h-10 w-auto"
            />
          </Link>
        </div>
      )}

      <h1 className="text-white text-2xl font-semibold mb-4">{t('pageTitle')}</h1>
      {/* List Layout */}
      <div className="flex flex-col items-center">
        {showSkeleton
          ? Array.from({ length: TOOL_SKELETON_COUNT }).map((_, index) => (
              <div key={index} className="w-full">
                <div
                  className="grid items-center gap-4 py-4 px-2"
                  style={{ gridTemplateColumns: '1fr 5fr 1fr' }}
                >
                  <div className="flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/10 animate-pulse" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="h-5 w-2/3 rounded bg-white/10 animate-pulse" />
                    <div className="h-4 w-full rounded bg-white/10 animate-pulse" />
                  </div>
                  <div className="flex items-center justify-end">
                    <div className="w-6 h-6 rounded bg-white/10 animate-pulse" />
                  </div>
                </div>
                {index < TOOL_SKELETON_COUNT - 1 && (
                  <div className="flex justify-center">
                    <div className="w-[75vw] h-px bg-pp-dark/25" />
                  </div>
                )}
              </div>
            ))
          : visibleTools.map((tool, index) => {
              const Icon = tool.icon;
              return (
                <div key={tool.id} className="w-full">
                  <Link
                    to={tool.href}
                    className="grid items-center gap-4 py-4 hover:bg-white/5 transition-all rounded-xl px-2"
                    style={{ gridTemplateColumns: '1fr 5fr 1fr' }}
                  >
                    <div className="flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
                        <Icon className="w-7 h-7 text-white/25" />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <p className="text-white text-xl font-bold">{tool.name}</p>
                      <p className="text-white/60 text-sm line-clamp-2">{tool.description}</p>
                    </div>
                    <div className="flex items-center justify-end">
                      <ChevronRight className="w-6 h-6 text-pp-orange" />
                    </div>
                  </Link>
                  {index < visibleTools.length - 1 && (
                    <div className="flex justify-center">
                      <div className="w-[75vw] h-px bg-pp-dark/25" />
                    </div>
                  )}
                </div>
              );
            })}
      </div>
    </>
  );
}
